import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronUp, ChevronRight, Calendar, Sun, Moon } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

export default function LegalPageLayout({ title, subtitle, children, tocItems }) {
  const { theme, toggle: toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      setShowTop(y > 400);
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (y / h) * 100 : 0);

      // Update active TOC
      let current = '';
      for (const item of tocItems) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) {
          current = item.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [tocItems]);

  useEffect(() => {
    // Reveal animations
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -30px 0px' }
    );
    document.querySelectorAll('.reveal').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [location.pathname]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-[#fafafa] font-['Inter',sans-serif] overflow-x-hidden">
      {/* Reading progress */}
      <div
        className="fixed top-0 left-0 h-[2px] z-[200] bg-gradient-to-r from-white/40 to-white/20"
        style={{ width: `${progress}%` }}
      />

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[99] bg-[#09090b]/97 backdrop-blur-xl flex flex-col items-center justify-center gap-6">
          <button
            onClick={() => setMobileOpen(false)}
            className="absolute top-5 right-5 text-zinc-500 hover:text-white p-2"
          >
            <X className="w-6 h-6" />
          </button>
          <Link to="/" className="text-xl text-zinc-400 hover:text-white" onClick={() => setMobileOpen(false)}>Home</Link>
          <Link to="/privacy" className="text-xl text-zinc-400 hover:text-white" onClick={() => setMobileOpen(false)}>Privacy</Link>
          <Link to="/terms" className="text-xl text-zinc-400 hover:text-white" onClick={() => setMobileOpen(false)}>Terms</Link>
          <Link to="/security" className="text-xl text-zinc-400 hover:text-white" onClick={() => setMobileOpen(false)}>Security</Link>
          <button onClick={() => { toggleTheme(); setMobileOpen(false); }} className="text-xl text-zinc-400 hover:text-white flex items-center gap-2">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            <span className="capitalize">{theme === 'dark' ? 'Light' : 'Dark'} mode</span>
          </button>
        </div>
      )}

      {/* Floating Navbar */}
      <nav
        className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-48px)] max-w-[900px] rounded-full px-6 py-3 flex items-center justify-between transition-all duration-400 border border-white/[0.06] ${
          scrolled
            ? 'bg-[#09090b]/95 shadow-[0_12px_50px_rgba(0,0,0,0.4)]'
            : 'bg-[#09090b]/60 backdrop-blur-2xl'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <img
            src="https://img.icons8.com/parakeet-line/48/tear-off-calendar.png"
            alt="ScheduleX"
            className="w-7 h-7"
          />
          <Link to="/" className="text-white font-bold text-lg tracking-tight">
            ScheduleX
          </Link>
        </div>
        <div className="hidden md:flex items-center gap-6">
          <Link to="/privacy" className="text-sm text-zinc-400 hover:text-white transition-colors">Privacy</Link>
          <Link to="/terms" className="text-sm text-zinc-400 hover:text-white transition-colors">Terms</Link>
          <Link to="/security" className="text-sm text-zinc-400 hover:text-white transition-colors">Security</Link>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="hidden sm:block text-sm text-zinc-400 hover:text-white transition-colors px-4 py-2"
          >
            Log in
          </Link>
          <button onClick={toggleTheme} className="text-sm text-zinc-400 hover:text-white px-2 py-2" aria-label="Toggle theme">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link
            to="/register"
            className="text-sm font-medium px-5 py-2 rounded-xl bg-white text-black hover:bg-zinc-200 transition-colors"
          >
            Start for free
          </Link>
          <button onClick={() => setMobileOpen(true)} className="md:hidden text-zinc-400 hover:text-white p-1">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </nav>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 w-11 h-11 rounded-xl bg-zinc-900 border border-white/[0.08] flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/20 transition-all z-50 ${
          showTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
        }`}
      >
        <ChevronUp className="w-5 h-5" />
      </button>

      {/* Header */}
      <header className="pt-32 pb-8 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6 flex-wrap text-sm text-zinc-500">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white font-medium">{title}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5">
            {title}
          </h1>
          <p className="text-lg text-zinc-500 max-w-xl leading-relaxed">{subtitle}</p>
          <div className="flex items-center gap-4 mt-6 flex-wrap">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-sm text-zinc-500">
              <Calendar className="w-4 h-4" />
              ScheduleX
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-sm text-zinc-500">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </div>
      </header>

      {/* Mobile TOC */}
      <div className="lg:hidden px-6 pb-4 overflow-x-auto">
        <div className="flex gap-2 w-max">
          {tocItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap border transition-all ${
                activeSection === item.id
                  ? 'bg-white/10 border-white/20 text-white'
                  : 'bg-transparent border-white/[0.06] text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="px-6 pb-24 relative z-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-12">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 max-h-[calc(100vh-140px)] overflow-y-auto pr-2 scrollbar-thin">
              <p className="text-[10px] font-semibold tracking-[0.08em] uppercase text-zinc-600 px-4 pb-3">
                On This Page
              </p>
              {tocItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className={`block w-full text-left px-4 py-2 text-[13px] border-l-2 transition-all leading-snug ${
                    activeSection === item.id
                      ? 'text-white border-white bg-white/[0.04]'
                      : 'text-zinc-600 border-transparent hover:text-zinc-400 hover:border-zinc-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Policy Content */}
          <div ref={contentRef} className="space-y-0">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.04] py-14 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-2.5 mb-4">
                <img
                  src="https://img.icons8.com/parakeet-line/48/tear-off-calendar.png"
                  alt="ScheduleX"
                  className="w-7 h-7"
                />
                <span className="text-white font-bold tracking-tight">ScheduleX</span>
              </Link>
              <p className="text-zinc-700 text-sm leading-relaxed">
                Scheduling made simple for modern teams.
              </p>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">
                Product
              </h4>
              <ul className="space-y-2.5">
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Integrations</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">
                Legal
              </h4>
              <ul className="space-y-2.5">
                <li><Link to="/privacy" className="text-sm text-zinc-600 hover:text-white transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="text-sm text-zinc-600 hover:text-white transition-colors">Terms</Link></li>
                <li><Link to="/security" className="text-sm text-zinc-600 hover:text-white transition-colors">Security</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">
                Resources
              </h4>
              <ul className="space-y-2.5">
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">API Docs</Link></li>
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Status</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-600 font-semibold mb-4">
                Company
              </h4>
              <ul className="space-y-2.5">
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">About</Link></li>
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/" className="text-sm text-zinc-600 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/[0.04] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-zinc-800">
              © {new Date().getFullYear()} ScheduleX. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
